/**
 * TabbedPage — 通用 Tab 頁面組件
 * 使用 antd Tabs + useSearchParams 實現 URL 可分享的 Tab 切換
 * Tab 狀態由 ?tab=xxx query param 控制，切換時用 router.replace 更新 URL
 *
 * 使用方式:
 * <TabbedPage
 *   tabs={[
 *     { key: 'list', label: '列表', children: <ListContent /> },
 *     { key: 'entry', label: '錄入', children: <EntryContent /> },
 *   ]}
 *   defaultTab="list"
 * />
 */
import { Suspense } from 'react';
import { useSearchParams, useRouter, usePathname } from '@/lib/navigation';
import { Tabs, Spin } from 'antd';
import type { TabsProps } from 'antd';

/** 單個 Tab 項配置 */
export interface TabItem {
  /** Tab 唯一標識，對應 URL ?tab=xxx */
  key: string;
  /** Tab 顯示標籤 */
  label: string;
  /** Tab 內容組件 */
  children: React.ReactNode;
  /** 是否禁用 */
  disabled?: boolean;
  /** Tab 標籤旁的 icon */
  icon?: React.ReactNode;
}

/** TabbedPage 組件屬性 */
interface TabbedPageProps {
  /** Tab 項配置數組 */
  tabs: TabItem[];
  /** 默認 Tab key（未指定 ?tab 時使用） */
  defaultTab?: string;
  /** antd Tabs 額外屬性透傳 */
  tabProps?: Omit<TabsProps, 'items' | 'activeKey' | 'onChange'>;
  /** 頁面標題（可選，顯示在 Tab 上方） */
  title?: string;
  /** 標題右側額外操作區（可選） */
  extra?: React.ReactNode;
}

/**
 * 內部 Tab 切換邏輯組件
 * 必須包裹在 Suspense 中（URL search params 狀態需要穩定 fallback）
 */
function TabbedPageInner({ tabs, defaultTab, tabProps, title, extra }: TabbedPageProps) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  /* 從 URL query 取得當前 tab，未設定時使用 defaultTab 或第一個 tab */
  const activeTab = searchParams.get('tab') || defaultTab || tabs[0]?.key || '';

  /**
   * Tab 切換處理：更新 URL query param
   * 使用 router.replace 避免產生瀏覽器歷史記錄
   */
  const handleTabChange = (key: string) => {
    const params = new URLSearchParams(searchParams.toString());
    params.set('tab', key);
    router.replace(`${pathname}?${params.toString()}`);
  };

  /* 轉換為 antd Tabs items 格式 */
  const items: TabsProps['items'] = tabs.map((tab) => ({
    key: tab.key,
    label: tab.label,
    children: tab.children,
    disabled: tab.disabled,
    icon: tab.icon,
  }));

  return (
    <div>
      {/* 標題區域（可選） */}
      {(title || extra) && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 16,
          }}
        >
          {title && (
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: '#1e293b' }}>{title}</h2>
          )}
          {extra && <div>{extra}</div>}
        </div>
      )}
      {/* Tab 切換 */}
      <Tabs activeKey={activeTab} onChange={handleTabChange} items={items} {...tabProps} />
    </div>
  );
}

/**
 * TabbedPage — 帶 Suspense 包裹的通用 Tab 頁面
 * 直接在 page.tsx 中使用即可
 */
export default function TabbedPage(props: TabbedPageProps) {
  return (
    <Suspense
      fallback={
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <Spin size="large" />
        </div>
      }
    >
      <TabbedPageInner {...props} />
    </Suspense>
  );
}
